import { useMemo } from 'react';
import { fmtLocalDate } from '../../../utils/datetime';
import { fuzzyMatch } from '../../../utils/fuzzyMatch';
import type { QuoteListRow } from './types';

/**
 * The toolbar Search box on both quote lists — legacy ALPACA puts a "Search"
 * field at the right-hand end of each list's bar.
 *
 * It matches against WHAT IS ON SCREEN, not against the raw row: the Date and
 * ExpirationDate are matched in their rendered `MM/DD/YYYY` form (via the shared
 * helper, never inline — §14b.6) and Prospect as "Yes"/"No". Searching the ISO
 * value instead would mean typing `09/15/2026` finds nothing while
 * `2026-09-15T19:29` finds the row — the opposite of what a user reading the
 * grid expects.
 *
 * `quotedAmount` is matched as a plain number (`100`), not as the formatted
 * `$100.00`, so both `100` and `$100` reach it.
 *
 * Matching is the SHARED fuzzy match (BO UI Standard v4 §9): case-insensitive
 * and tolerant of gaps, so "cohou" finds "City of Houston". Each column is
 * matched on its own, so letters are never stitched together across two
 * columns into a false hit.
 *
 * Client-side, over rows already fetched. That matches the legacy box, which
 * filters the grid it is attached to; it does NOT widen the Date Filter, so a
 * quote outside the chosen window stays out until the chip is widened.
 */
export function useQuoteSearch(rows: QuoteListRow[], search: string): QuoteListRow[] {
  return useMemo(() => {
    const q = search.trim();
    if (!q) return rows;

    return rows.filter((r) => {
      const haystack = [
        fmtLocalDate(r.quotedDate),
        r.division,
        r.quoteID,
        r.title,
        r.client,
        r.isProspect ? 'yes' : 'no',
        r.quotedAmount == null ? '' : String(r.quotedAmount),
        fmtLocalDate(r.expirationDate),
        r.status,
      ];
      return haystack.some((v) => fuzzyMatch(q, v ?? ''));
    });
  }, [rows, search]);
}
